import { get, send, type StudentHubInvitations } from "./api.js";
import type { SchoolPupil } from "./schoolGrooves.js";

/**
 * The staff-facing School pupil record.
 *
 * A School pupil is not a private student: teaching records hang off the
 * pupil, while contact, fee and invoicing hang off a private record that may
 * be bridged to it. The profile read carries both halves, clearly separated.
 */

export interface SchoolPupilDetails {
  id: string;
  schoolKey: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  fullName: string;
  email: string | null;
  phone: string | null;
  status: "pending" | "active" | "ended" | "archived" | string;
  instrument: string | null;
  formClass: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  version: number;
}

export interface SchoolPupilPrivateRecord {
  familyOverviewAvailable?: boolean;
  studentId: string;
  fullName: string;
  status: string;
  hasAccount: boolean;
  /** False for a School-only record, which the private student pages do not show. */
  hasPrivatePage: boolean;
  instrument: string | null;
  formClass: string | null;
  lessonLength: string | null;
  tutorName: string | null;
  serviceName: string | null;
  studentEmail: string | null;
  parentName: string | null;
  parentEmail: string | null;
  parentPhone: string | null;
  schoolEmail: string | null;
  studentCode: string | null;
}

export interface SchoolPupilAssignment {
  id: string;
  tutorProfileId: string | null;
  tutorName: string | null;
  serviceName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  status: "active" | "pending-review" | "ended" | "rejected" | string;
  createdReason: string | null;
  endedReason: string | null;
}

export interface SchoolPupilUpcomingLesson {
  sessionId: string;
  schoolKey: string;
  startsAt: string;
  endsAt: string;
  serviceName: string | null;
  status: string;
}

export interface SchoolPupilNoteState {
  id: string;
  title: string | null;
  publishedAt: string | null;
  familyNotifiedAt: string | null;
  updatedAt: string | null;
}

export interface SchoolPupilPastLesson extends SchoolPupilUpcomingLesson {
  cancelled: boolean;
  attendanceStatus: string | null;
  note: SchoolPupilNoteState | null;
}

export interface SchoolPupilRepertoireEntry {
  id: string;
  libraryPieceId?: string | null;
  familyVisible?: boolean;
  title: string;
  composer: string | null;
  workOrCollection: string | null;
  instrument: string | null;
  notes: string | null;
  status: "learning" | "polishing" | "performance_ready" | "completed" | "paused" | string;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string | null;
}

export interface SchoolPupilPracticeTask {
  materials?: import("@wmf/shared/practiceReview").PracticeTaskMaterial[];
  id: string;
  title: string;
  description: string | null;
  completed: boolean;
  completedAt: string | null;
  teacherNotes: string | null;
  suggestedMinutes: number | null;
  repertoireTitle: string | null;
  sourceSessionId: string | null;
  createdAt: string | null;
}

export interface SchoolPupilBilling {
  lessonRate: string | null;
  invoicedLessons: number;
  outstandingLessons: number;
  outstandingAmount: string;
}

export interface SchoolPupilProfile {
  pupil: SchoolPupilDetails;
  parentEmailEditable?: boolean;
  parentPhoneEditable?: boolean;
  hubInvitations?: StudentHubInvitations | null;
  privateRecord: SchoolPupilPrivateRecord | null;
  assignments: SchoolPupilAssignment[];
  upcoming: SchoolPupilUpcomingLesson[];
  history: SchoolPupilPastLesson[];
  upcomingPage?: number;
  upcomingLimit?: number;
  upcomingTotal?: number;
  upcomingPages?: number;
  historyPage?: number;
  historyLimit?: number;
  historyTotal?: number;
  historyPages?: number;
  nextLesson?: SchoolPupilUpcomingLesson | null;
  nextWeekLessons?: SchoolPupilUpcomingLesson[];
  repertoire: SchoolPupilRepertoireEntry[];
  currentRepertoire?: SchoolPupilRepertoireEntry[];
  completedRepertoire?: SchoolPupilRepertoireEntry[];
  repertoireHistoryPage?: number;
  repertoireHistoryLimit?: number;
  repertoireHistoryTotal?: number;
  repertoireHistoryPages?: number;
  practice: SchoolPupilPracticeTask[];
  currentPractice?: SchoolPupilPracticeTask[];
  completedPractice?: SchoolPupilPracticeTask[];
  practiceHistoryPage?: number;
  practiceHistoryLimit?: number;
  practiceHistoryTotal?: number;
  practiceHistoryPages?: number;
  billing: SchoolPupilBilling | null;
  summary: {
    lessonsTaught: number;
    notesPublished: number;
    notesOutstanding: number;
    notesSentToFamily: number;
    lastLessonAt: string | null;
    nextLessonAt: string | null;
  };
}

export async function sendSchoolHubInvitations(
  pupilId: string,
  recipients: string[],
  resend = false,
): Promise<{ hubAccess: StudentHubInvitations }> {
  return send("POST", `/school-domain/pupils/${encodeURIComponent(pupilId)}/hub-invitations`, { recipients, resend });
}

export interface SchoolPupilEdit {
  firstName?: string;
  lastName?: string;
  preferredName?: string | null;
  email?: string | null;
  parentEmail?: string | null;
  parentPhone?: string | null;
  expectedParentEmail?: string | null;
  expectedParentPhone?: string | null;
  expectedParentStudentId?: string;
  phone?: string | null;
  status?: "pending" | "active" | "ended" | "archived";
  instrument?: string | null;
  formClass?: string | null;
  version: number;
}

export async function getSchoolPupilProfile(
  pupilId: string,
  params?: {
    upcomingPage?: number;
    upcomingLimit?: number;
    historyPage?: number;
    historyLimit?: number;
    practiceHistoryPage?: number;
    practiceHistoryLimit?: number;
    repertoireHistoryPage?: number;
    repertoireHistoryLimit?: number;
  },
): Promise<SchoolPupilProfile> {
  return get<SchoolPupilProfile>(`/school-domain/pupils/${encodeURIComponent(pupilId)}`, params
    ? Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      )
    : undefined);
}

export async function updateSchoolPupil(
  pupilId: string,
  edit: SchoolPupilEdit,
): Promise<{ pupil: SchoolPupilDetails }> {
  return send<{ pupil: SchoolPupilDetails }>(
    "PATCH",
    `/school-domain/pupils/${encodeURIComponent(pupilId)}`,
    edit,
  );
}

/** Active pupils with no active teaching assignment: nobody's follow-up list will show them. */
export async function listUnassignedSchoolPupils(): Promise<{ pupils: SchoolPupil[] }> {
  return get<{ pupils: SchoolPupil[] }>("/school-domain/pupils", { unassigned: "1" });
}

/** Every active School pupil, across every school. */
export async function listAllSchoolPupils(): Promise<{ pupils: SchoolPupil[] }> {
  return get<{ pupils: SchoolPupil[] }>("/school-domain/pupils");
}

/** Every active pupil at one school, by its lower-cased key. */
export async function listSchoolPupilsAt(schoolKey: string): Promise<{ pupils: SchoolPupil[] }> {
  return get<{ pupils: SchoolPupil[] }>("/school-domain/pupils", { school: schoolKey });
}

/** Active pupils whose name contains the query, across every school. */
export async function searchSchoolPupils(q: string): Promise<{ pupils: SchoolPupil[] }> {
  return get<{ pupils: SchoolPupil[] }>("/school-domain/pupils", { q });
}

/** The pupil's profile page. */
export function schoolPupilHref(pupilId: string): string {
  return `/school-pupils/${encodeURIComponent(pupilId)}`;
}

/** The School lesson write-up for one session and pupil. */
export function schoolLessonHref(sessionId: string, pupilId: string): string {
  const params = new URLSearchParams({ writeup: "1", session: sessionId, pupil: pupilId });
  return `/schools?${params.toString()}`;
}

export function schoolPupilDisplayName(
  pupil: Pick<SchoolPupil, "firstName" | "lastName" | "preferredName">,
): string {
  return `${pupil.preferredName?.trim() || pupil.firstName} ${pupil.lastName}`.trim();
}

/** How a School lesson's note stands, as the profile and Home show it. */
export type SchoolNoteState = "none" | "draft" | "published" | "sent";

export function schoolNoteState(note: SchoolPupilNoteState | null): SchoolNoteState {
  if (!note) return "none";
  if (note.familyNotifiedAt) return "sent";
  if (note.publishedAt) return "published";
  return "draft";
}

export const SCHOOL_NOTE_STATE_LABELS: Record<SchoolNoteState, string> = {
  none: "No note",
  draft: "Draft",
  published: "Published",
  sent: "Sent to family",
};
